import { useEffect, useMemo, useState } from "react";
import { Search, DollarSign, X } from "lucide-react";
import api from "../lib/api";
import { fmtBRL, HEADER_BG } from "../lib/constants";

export default function PriceLookupModal({ initialQuery = "", onClose, onPick }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(initialQuery || "");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/price-list")
      .then((r) => setItems(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Erro ao carregar tabela de preços."))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return items;
    return items.filter((it) =>
      (it.device || "").toLowerCase().includes(s) ||
      (it.service || "").toLowerCase().includes(s) ||
      (it.notes || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="price-lookup-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] shadow-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-700 to-blue-900 text-white flex items-center justify-between">
          <div className="inline-flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            <div>
              <h3 className="font-heading font-bold text-base leading-tight">Consultar tabela de preços</h3>
              <p className="text-[11px] text-white/80 mt-0.5">
                {onPick ? "Clique num item para preencher automaticamente" : "Só leitura — para conferência rápida"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="price-lookup-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar aparelho, serviço, observação..."
              data-testid="price-lookup-search"
              className="w-full h-10 rounded-lg border border-slate-300 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-400">Carregando...</div>
          ) : err ? (
            <div className="p-6 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg m-4">{err}</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400 italic">
              {items.length === 0 ? "Nenhum preço cadastrado ainda. Cadastre na aba Tabela de preços." : "Nenhum resultado para essa busca."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0">
                <tr style={{ background: HEADER_BG }}>
                  {["APARELHO", "SERVIÇO / PEÇA", "CUSTO", "VENDA", "MARGEM", "OBS."].map((h) => (
                    <th key={h} className="text-left text-[10px] font-bold uppercase tracking-wider text-white px-3 py-2 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const margin = (Number(it.price) || 0) - (Number(it.cost) || 0);
                  const clickable = !!onPick;
                  return (
                    <tr
                      key={it.id}
                      onClick={() => clickable && onPick(it)}
                      data-testid={`price-lookup-row-${it.id}`}
                      className={`border-t border-slate-100 ${clickable ? "hover:bg-blue-50/60 cursor-pointer" : "hover:bg-slate-50/40"}`}
                    >
                      <td className="px-3 py-2 text-xs font-semibold text-slate-800">{it.device || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-700">{it.service || "—"}</td>
                      <td className="px-3 py-2 text-xs text-rose-700 tabular-nums whitespace-nowrap">R$ {fmtBRL(it.cost)}</td>
                      <td className="px-3 py-2 text-xs font-semibold text-blue-700 tabular-nums whitespace-nowrap">R$ {fmtBRL(it.price)}</td>
                      <td className="px-3 py-2 text-xs font-semibold text-emerald-700 tabular-nums whitespace-nowrap">R$ {fmtBRL(margin)}</td>
                      <td className="px-3 py-2 text-[11px] text-slate-500 max-w-[220px] truncate" title={it.notes}>
                        {it.notes || <span className="italic text-slate-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end">
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-200"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
